import EventDispatcher from "./EventDispatcher";
import { GameDataRequests, InstructionIntent, WrapperEvents } from "./constants/WrapperMessages";
import { FrameworkMessenger } from "./FrameworkMessenger";

const FW = window.IntouchGames.GameFramework;

export const enum PopupId {
    LOADING,
    ERROR,
    INCOMPATIBLE_BROWSER,
    SERVER_AVAILABILITY,
    ERROR_INSUFFICIENT_BALANCE,
    WIN_CAPPING,
    FREE_BETS,
    JACKPOT,
    WIN_CAPPING_GAMBLE,
}

export enum MessageBoxID
{
    WelcomeMessage = "WelcomeMessage",
    RepeatWelcomeMessage = "RepeatWelcomeMessage",
    FreeSpinsBetLines = "FreeSpinsBetLines",
    FreeSpinsGambleThresholdButton = "FreeSpinsGambleThresholdButton",
    NotEnoughCreditMessage = "NotEnoughCreditMessage",
    NotEnoughCreditMessageReels = "NotEnoughCreditMessageReels",
    NotEnoughCreditMessageLines = "NotEnoughCreditMessageLines",
    AutospinWinJackpot = "AutospinWinJackpot",
    AutospinLossLimit = "AutospinLossLimit",
    AutospinWinLimit = "AutospinWinLimit",
    AutoSpinLimitNotSet = "AutoSpinLimitNotSet",
    AutospinInsufficientCredit = "AutospinInsufficientCredit",
    AutospinInsufficientCreditStart = "AutospinInsufficientCreditStart",
    NetworkError = "Network Error",
    ServerTimeout = "Server Timeout",
    NoQDPermission = "NoQDPermission",
    MaxBetFreeSpins = "MaxBetFreeSpins",
    MaxBetAutoSpins = "MaxBetAutoSpins",
    MaxBetBetLinesReset = "MaxBetBetLinesReset",
    MaxBetBetReset = "MaxBetBetReset",
    MaxBetNotEnoughCredit = "MaxBetNotEnoughCredit",
    LoadingGameSettings = "LoadingGameSettings",
}

type FWMessagesID = keyof typeof MessageBoxID;

export default class WrapperDispatcher
{
    private api: any;
    private dispatcher = new EventDispatcher();

    private autoSpinStarted: boolean = false;
    private balanceAnimationEnabled: boolean = true;

    private amountWon: number = 0;
    private credit: number = -1;
    private shouldDisableBalanceCheck: boolean = false;
    public instantJpMinigameActive: boolean = false;

    // To remove after framework proper fix
    private readonly minDurationDelay: number = 200;

    private forces: any;

    private externalPopUpIdMap: { [key: string]: PopupId } = {
        [MessageBoxID.NotEnoughCreditMessageReels]: PopupId.ERROR_INSUFFICIENT_BALANCE,
        [MessageBoxID.AutospinInsufficientCredit]: PopupId.ERROR_INSUFFICIENT_BALANCE,
        [MessageBoxID.AutospinInsufficientCreditStart]: PopupId.ERROR_INSUFFICIENT_BALANCE,
        [MessageBoxID.MaxBetNotEnoughCredit]: PopupId.ERROR_INSUFFICIENT_BALANCE
    };
    private lastPopUpIdShown: string | null = null;

    public dispatch(message: string, data?: any)
    {
        this.dispatcher.dispatch(message, data);
    }

    public init(): void
    {
        this.api.removeSplash()
        this.hideClock();
        this.api.updateUIElement("CLOCK", { visible: true });
        this.api.visibilityMenuItem("help", true);
        this.api.onChangeSoundsVolume((volume: number) => {
            this.handleSoundToggle(volume)
        })

        this.api.onResize((size: { width: string, height: string }) => {
            const { width, height } = size;

            const div: HTMLElement | null = document.querySelector("#game");
            if (div !== null)
            {
                div.style.width = width + "px";
                div.style.height = height + "px";

                setTimeout(() => {
                    window.dispatchEvent(new Event("resize"));
                }, 200);
            }
        });
    }

    public initContext(): void
    {
        window.swContext = {
            context: {
                kernel: {},
                gameInitSettings: {
                    gameSettingsConfig: {
                        ID: "sw_stub_mi_dr"
                    }
                },
                api: this.api
            },
            PIXI: {
                ticker: {
                    shared: window.PIXI.sharedTicker
                }
            },
            dispatcher: this.dispatcher
        };
    }

    public initDispatcher(): void
    {
        this.api = window.wrapperAPI;
    }

    public addListeners()
    {
        this.dispatcher.addListener(GameDataRequests.GET_CORE_MODULE_STATE, this.onGetModuleType);
        this.dispatcher.addListener(GameDataRequests.GET_GAME_MODE_STATE, this.onGetGameModeState);
        this.dispatcher.addListener(GameDataRequests.GET_TOTAL_WIN, this.onGetTotalWin);
        this.dispatcher.addListener(GameDataRequests.GET_TOTAL_BET, this.onGetTotalBet);
        this.dispatcher.addListener(GameDataRequests.GET_BALANCE, this.onGetBalance);
        this.dispatcher.addListener(GameDataRequests.GET_COINS_STATE, this.onGetCoinState);
        this.dispatcher.addListener(InstructionIntent.SET_BALANCE, this.onSetBalance);
        this.dispatcher.addListener(InstructionIntent.DISABLE_AUTOSPIN, this.resetAutoSpins);
        this.dispatcher.addListener(InstructionIntent.PAUSE_GAME, this.pause);
        this.dispatcher.addListener(InstructionIntent.RESUME_GAME, this.unpause);
        this.dispatcher.addListener(InstructionIntent.ENABLE_BALANCE_CHECK, this.enableBalanceCheck);
        this.dispatcher.addListener(InstructionIntent.DISABLE_BALANCE_CHECK, this.disableBalanceCheck);
        this.dispatcher.addListener(InstructionIntent.START_SPIN, this.onStartSpin);
        this.dispatcher.addListener(InstructionIntent.DISABLE_AUDIO, this.disableAudio);
        this.dispatcher.addListener(InstructionIntent.ENABLE_AUDIO, this.enableAudio);
        this.dispatcher.addListener(InstructionIntent.SHOW_PAY_TABLE, this.showPaytable);
        this.dispatcher.addListener(InstructionIntent.HIDE_PAY_TABLE, this.hidePaytable);
        this.dispatcher.addListener(WrapperEvents.SHOW_PAYTABLE, this.showPaytable);
        this.dispatcher.addListener(WrapperEvents.HIDE_PAYTABLE, this.hidePaytable);
    }

    public addApiListeners(): void {
        this.api.dispatcher.addListener(WrapperEvents.ONPAYTABLE, this.showPaytable);
        this.api.dispatcher.addListener(WrapperEvents.GO_LOBBY, this.closeGame);
        this.api.dispatcher.addListener(WrapperEvents.CLOSE_WINDOW, this.closeGame);
        this.api.dispatcher.addListener(WrapperEvents.MINI_GAME_ACTIVATED, this.jpMinigameActivated);
        this.api.dispatcher.addListener(WrapperEvents.MINI_GAME_ENDED, this.jpMinigameEnded);
    }

    public setAmountWon(newValue: number): void
    {
        this.amountWon = newValue;
    }

    public setAutospinsStarted(areStarted: boolean)
    {
        this.autoSpinStarted = areStarted;
    }

    public setMainMenuState(): void
    {
        this.api.updateMenuState({open: true, visible: true });
        this.api.setMenuOptions({ hideButton: true });
    }

    public async sendResumeResponse(): Promise<void>
    {
        let response = await this.api.getInitResponse();
        const mergedSettings = await this.api.getMergedSettings();

        response = await this.applyRegulationRules(response, mergedSettings)

        response.gameCode = this.api.token.gameCode;

        this.checkFiswn(response);

        const message = new FW.GameResponseMessage()
        message.response = response;
        FrameworkMessenger.sendMessage(message);

        this.handleCreditVisibility();

        setTimeout(() => {
            this.dispatcher.dispatch("GameStateEvents.GAME_LOADED");
            this.setHistoryParams(mergedSettings);

            if(this.shouldDisableBalanceCheck) {
                this.disableBalanceCheck();
            }

            this.sendEnterGameMessage();
        }, 200)
    }

    public async sendGameResponse(payload: any): Promise<void>
    {
        const sequenceForce = this.forces?.getSequenceForceToAppend();
        if (sequenceForce !== undefined)
            payload.positions = sequenceForce;

        const response = await this.api.spin(payload);

        if (response === null) {
            return;
        }

        const message = new FW.GameResponseMessage()
        message.response = response;
        FrameworkMessenger.sendMessage(message);

        if (this.instantJpMinigameActive)
        {
            if (this.autoSpinStarted)
                this.stopAutospin();

            this.disableSpinButton();
        }
    }

    public formatNumber(value: number, settings: any): string
    {
        return this.api.moneyFormatter.formatNumber(value, settings);
    }

    private onGetModuleType = (data: any) => {
        data.isSet = true;
        data.moduleType = "GameTypes.GENERATION_2";
    }

    private onGetGameModeState = (data: any) => {
        data.modes = [];
        data.isUntilFeature = false;

        data.modes.push("GameModesList.TURBO_OFF"); // Currently not support turbo switch.

        if (this.autoSpinStarted)
        {
            data.modes.push("GameModesList.AUTO_SPIN_MODE");

            const message = new FW.GetAutoSpinsNumberMessage();

            if (message.ask !== undefined)
            {
                data.isSet = true;
                data.autoSpinsNumber = message.ask();
            }
            else
            {
                message.returnBetCallback = (autoSpinsNumber: number) =>
                {
                    data.isSet = true;
                    data.autoSpinsNumber = autoSpinsNumber;
                }
                FrameworkMessenger.sendMessage(message);
            }
        }
        else
        {
            data.isSet = true;
        }
    }

    private onGetTotalWin = (data: any) => {
        data.isSet = true;
        data.totalWin = this.amountWon / 100;
    }

    private onGetTotalBet = (data: any) => {
        const message = new FW.GetTotalBetMessage();
        message.returnBetCallback  = (totalBet: number) => {
            data.isSet = true;
            data.totalBet = totalBet > 0 ? totalBet / 100 : 0;
        }
        FrameworkMessenger.sendMessage(message);
    }

    private onSetBalance(data: any) {
        let message;
        const cashCredit: number = data.balance * 100;
        if (FW.SetCreditMessage !== undefined)
        {
            message = new FW.SetCreditMessage();
            message.cashCredit = cashCredit;
            message.bonusCredit = 0;
        }
        else if (FW.CreditChangedModuleMessage !== undefined)
        {
            message = new FW.CreditChangedModuleMessage(cashCredit);
        }

        if (message !== undefined)
            FrameworkMessenger.sendMessage(message);
    }

    public pause = () => {
        FW.ScreenVisibility.externalPauseResumeEvents(true);
        this.dispatcher.dispatch("GameStateEvents.GAME_PAUSED");
    }

    public unpause() {
        FW.ScreenVisibility.externalPauseResumeEvents(false);
    }

    private enableBalanceCheck()
    {
        const message = new FW.IgnoreCreditCheckMessage()
        message.ignoreCreditCheck = false;
        FrameworkMessenger.sendMessage(message);
    }

    private disableBalanceCheck = () =>
    {
        this.shouldDisableBalanceCheck = true;
        const message = new FW.IgnoreCreditCheckMessage()
        message.ignoreCreditCheck = true;
        FrameworkMessenger.sendMessage(message);
    }

    public sendEnterGameMessage()
    {
        this.dispatcher.dispatch("GameStateEvents.ENTER_GAME");
    }

    public notifyAboutCreditChange(value: number): void {
        if (value !== this.credit)
        {
            this.credit = value;
            this.api.updateViewBalance(value/100);
        }
    }

    public forceCreditChangeNotification(): void {
            this.api.updateViewBalance(this.credit/100);
    }

    public showPaytable = () => {
        const message = new FW.OpenPaytableExternalMessage()
        message.open = true;
        FrameworkMessenger.sendMessage(message);
        this.api.updateMenuState({open: false, visible: false });
    }

    private handleSoundToggle = (isOn: number) => {  //0 - disabled 1 - enabled
        if (isOn == 0) {
            this.disableAudio();
        } else {
            this.enableAudio();
        }
    }

    public enableAudio()
    {
        FW.SoundControls.isSoundEnabled = true;
    }

    public disableAudio()
    {
        FW.SoundControls.isSoundEnabled = false;
    }

    private hideClock() {
        if (FW.ExternalClockVisibility !== undefined)
        {
            const message = new FW.ExternalClockVisibility()
            message.show = false;
            FrameworkMessenger.sendMessage(message);
        }
        else if (FW.ExternalHideClockMessage !== undefined)
        {
            FrameworkMessenger.sendMessage(new FW.ExternalHideClockMessage());
        }
    }

    public setAutospins() {
        FrameworkMessenger.sendMessage(new FW.StartAutospin());
    }

    public resetAutoSpins() {
        FrameworkMessenger.sendMessage(new FW.StopAutospin());
    }

    public hidePaytable() {
        const message = new FW.OpenPaytableExternalMessage()
        message.open = false;
        FrameworkMessenger.sendMessage(message);
    }

    public closeGame() {
        const bodyElem: HTMLElement = document.getElementsByTagName("body")[0];
        bodyElem.style.height = "100vh";
    }

    public sendTitleChangedNotification(title: string) {
        if (this.api.showGameName !== undefined) {
            this.api.showGameName({gameName: title});
        }
    }

    protected onGameLoaded(): void {
        this.dispatch("GameStateEvents.GAME_LOADED");
    }

    private onStartSpin(): void {
        FrameworkMessenger.sendMessage(new FW.RemoteSpinReelsModuleMessage());
    }

    private onGetBalance = (data: any) => {
        const message = new FW.GetCurrentCreditQueryMessage();

        if (message.ask !== undefined)
        {
            data.isSet = true;
            data.balance = message.ask() / 100;
        }
        else
        {
            message.returnCreditCallback  = (currentCredit: number) => {
                data.isSet = true;
                data.balance = currentCredit / 100;
            }
            FrameworkMessenger.sendMessage(message);
        }
    }

    private onGetCoinState = (data: any) => {
        const message = new FW.GetBetInfoQueryMessage();

        if (message.ask !== undefined)
        {
            data.isSet = true;

            const betInfo = message.ask();

            data.availableCoins = betInfo.availableCoins;
            data.coinValue = betInfo.coinValue;
            data.currentBetsIndex = betInfo.currentBetsIndex;
        }
    }

    private async setHistoryParams(mergedSettings: any) {
        const initResponse = await this.api.getInitResponse();

        let time = new Date();
        let timeZoneParam = Math.floor(time.getTimezoneOffset() / 60);

        let licenseeName: string = "";
        if (FW.B2BNetworkerModule !== undefined && FW.B2BNetworkerModule.licenseName !== undefined)
            licenseeName = FW.B2BNetworkerModule.licenseName;
        else if (FW.NetworkerModule !== undefined && FW.NetworkerModule.licenseName !== undefined)
            licenseeName = FW.NetworkerModule.licenseName;

        const params: any = {
            LicenseeName: licenseeName,
            AccountID: encodeURIComponent(initResponse.loginResponse.AccountID),
            SessionID: initResponse.loginResponse["x-slotfactory-sessionid"],
            GameName: FW.NetworkerApp.gameName,
            GameTitle: FW.GameInfo.config.genericConfig.gameTitle,
            Timestamp: time.getTime(),
            Locale: FW.Multilanguage.currentLocale,
            TimeZone: "UTC" + encodeURIComponent(timeZoneParam > 0 ? "-" : "+") + Math.abs(timeZoneParam)
        };

        if (mergedSettings.showBalanceBeforeAndAfter === true)
            params.showBalanceBeforeAndAfter = true;

        this.api.setHistoryParams(params);
    }

    private async applyRegulationRules(response: any, mergedSettings: any): Promise<any> {
        const parsedResponse = response;

        const initResponse = await this.api.getInitResponse();

        parsedResponse.Config_AutoplayAvailable = this.getAutoplayMode(mergedSettings.autoplayMode);

        parsedResponse.Config_WinMultiplierThreshold = this.getWinMultiplierThreshold(initResponse.loginResponse.SWResponse.jrsdSettings);

        const minSpinTime: number | undefined = this.getConfigMinSpinTime(initResponse.loginResponse.SWResponse.jrsdSettings);
        if (minSpinTime !== undefined)
            parsedResponse.Config_MinSpinTime = minSpinTime;

        this.setBalanceAnimationMode(initResponse.loginResponse.SWResponse.brandSettings);

        this.setInfiniteAutoSpin(mergedSettings);

        return parsedResponse;
    }

    private getAutoplayMode(autoPlayEnabled: boolean | undefined): number {
        if (autoPlayEnabled)
            return 1;

        return 0;
    }

    private getWinMultiplierThreshold(jrsdSettings: any): number {
        let prohibitingFalseWins: boolean | undefined = false;

        if (jrsdSettings !== undefined)
            prohibitingFalseWins = jrsdSettings.prohibitingFalseWins;

        if (prohibitingFalseWins)
            return 1;

        return 0;
    }

    private getConfigMinSpinTime(jrsdSettings: any): number | undefined
    {
        let minRoundDuration: number | undefined;

        if ((jrsdSettings !== undefined) && (jrsdSettings.minRoundDuration !== undefined))
            minRoundDuration = jrsdSettings.minRoundDuration + this.minDurationDelay;

        return minRoundDuration;
    }

    private setBalanceAnimationMode(brandSettings: any): void {

        if ((brandSettings !== undefined) && (brandSettings.balanceAnimation !== undefined))
            this.balanceAnimationEnabled = brandSettings.balanceAnimation;

        if (this.balanceAnimationEnabled)
            this.enableFlashCredit();
        else
            this.disableFlashCredit();
    }

    private disableFlashCredit() {
        if (FW.DisableFlashCreditMessage !== undefined)
        {
            const disableFlashCreditMessage = new FW.DisableFlashCreditMessage();
            disableFlashCreditMessage.reason = FW.FlashCreditInhibitor.CustomGameReason;
            FrameworkMessenger.sendMessage(disableFlashCreditMessage);
        }
    }

    private enableFlashCredit() {
        if (FW.EnableFlashCreditMessage !== undefined)
        {
            const enableFlashCreditMessage = new FW.EnableFlashCreditMessage();
            enableFlashCreditMessage.reason = FW.FlashCreditInhibitor.CustomGameReason;
            FrameworkMessenger.sendMessage(enableFlashCreditMessage);
        }
    }

    private setInfiniteAutoSpin(mergedSettings: any): void
    {
        const infiniteAutoSpin = mergedSettings.autoPlayUntilFeatureEnabled;
        const message = new FW.AutoSpinStateInfiniteMessage()
        message.infinite = infiniteAutoSpin;
        FrameworkMessenger.sendMessage(message);
    }

    private handleCreditVisibility(): void
    {
        let state: { visible?: boolean } = this.api.getUIElementState("BALANCE");
        if (state && state.visible === false && (!FW.Environment.isMobile())) {
            this.hideCredit();
        }
    }

    private hideCredit() {
        if (FW.ExternalCreditPanelVisibility !== undefined)
        {
            const message = new FW.ExternalCreditPanelVisibility()
            message.show = false;
            FrameworkMessenger.sendMessage(message);
        }
        else if (FW.ExternalHideCreditMessage !== undefined)
        {
            FrameworkMessenger.sendMessage(new FW.ExternalHideCreditMessage());
        }

        this.disableFlashCredit();
    }

    private showCredit() {
        if (FW.ExternalCreditPanelVisibility !== undefined)
        {
            const message = new FW.ExternalCreditPanelVisibility()
            message.show = true;
            FrameworkMessenger.sendMessage(message);
        }
        else if (FW.ExternalShowCreditMessage !== undefined)
        {
            FrameworkMessenger.sendMessage(new FW.ExternalShowCreditMessage());
        }

        if (this.balanceAnimationEnabled)
            this.enableFlashCredit();
    }

    private jpMinigameActivated = (minigame: any): void =>
    {
        if (minigame?.miniGameType === "instant-jp-mini-game")
            this.instantJpMinigameActive = true;
    }

    private jpMinigameEnded = (): void =>
    {
        if (this.instantJpMinigameActive)
        {
            this.instantJpMinigameActive = false;
            this.enableSpinButton();
        }
    }

    private enableSpinButton()
    {
        if (FW.GameEnableMainScreenInputMessage !== undefined)
            FrameworkMessenger.sendMessage(new FW.GameEnableMainScreenInputMessage());
    }

    private disableSpinButton()
    {
        if (FW.GameDisableMainScreenInputMessage !== undefined)
            FrameworkMessenger.sendMessage(new FW.GameDisableMainScreenInputMessage());
    }

    private stopAutospin(): void
    {
        if (FW.StopAutospin !== undefined)
        {
            const message = new FW.StopAutospin();
            FrameworkMessenger.sendMessage(message);
        }
    }

    private resumeAutospin(): void
    {
        if (FW.SetAutoSpinsPauseStateMessage !== undefined)
        {
            const message = new FW.SetAutoSpinsPauseStateMessage();
            message.isPaused = false;
            FrameworkMessenger.sendMessage(message);
        }
    }

    public useExternalMessageBox(message: any): boolean
    {
        if (message === undefined)
            return false;

        if (message.serverMsgName === undefined)
            return false;

        if (this.externalPopUpIdMap[message.serverMsgName] === undefined)
            return false;

        return true;
    }

    public showPopup(message: any): void {
        const popUpId: number = message.serverMsgName !== undefined &&
            this.externalPopUpIdMap[message.serverMsgName] !== undefined ? this.externalPopUpIdMap[message.serverMsgName] : -1;

        if (popUpId >= 0)
        {
            setTimeout(() => {
                FrameworkMessenger.sendMessage(new FW.HideMessageBoxMessage());
            }, 0);

            this.api.showPopup(popUpId);
        }
        else
        {
            this.lastPopUpIdShown = this.api.showPopup({
                preventHideOnClick: message.dismissible,
                message: message.messageText,
                cbClose: () => {
                    this.lastPopUpIdShown = null;
                    FrameworkMessenger.sendMessage(new FW.HideMessageBoxMessage());
                }
            });
        }
    }

    public hidePopup(): void {
        if (this.lastPopUpIdShown !== null)
        {
            this.api.hidePopup(this.lastPopUpIdShown);
            this.lastPopUpIdShown = null;
        }
    }

    private async checkFiswn(response: any): Promise<void>
    {
        const fiswnActive = FW.GameParameters.getValue("fk");
        if (fiswnActive === undefined || fiswnActive.length === 0)
            return;

        if (!this.haveFwFiswn())
        {
            const version: string = await this.getFiswnVersion();

            const scriptElement: HTMLScriptElement = document.createElement("script");
            scriptElement.src = "/fiswn/" + version + "/index.js";
            scriptElement.type = "text/javascript";

            scriptElement.onload = (): void => {
                this.forces = fiswn.init(response);
            };

            scriptElement.onerror = (): void => { };

            document.getElementsByTagName("head")[0].appendChild(scriptElement);
        }
    }

    private async getFiswnVersion(): Promise<string>
    {
        const fiswnVersion: string | undefined = FW.GameParameters.getValue("fkv");
        if (fiswnVersion !== undefined && fiswnVersion.length > 0)
            return fiswnVersion;

        const origin: string = window.location.origin;
        const url: string = `${origin}/fiswn/versionInfo.json`;
        const res: Response = await fetch(url);
        const json = await res.json();
        if (json.build !== undefined  && json.build.length > 0)
            return json.build;

        return "1.0.0";
    }

    private haveFwFiswn(): boolean
    {
        if (FW.Debug === undefined)
            return false;

        if (FW.Debug.ForcesPanel === undefined)
            return false;

        if (FW.debugAvailable === undefined)
            return false;

        if (FW.GameSettings.disableFrameworkForces === true)
            return false;

        return FW.debugAvailable();
    }
}
